import PassengerInfo from '@/components/purchase/checkout/PassengerInfo';
import { type PersonDataField } from '@/hooks/usePersonFieldRenderer';
import { TransText } from '@/i18n/trans/text';
import type { CheckoutPassenger } from '@/types/passenger';
import { getPassengerName } from '@/utils/passenger';
import type { CheckoutValues } from '@/utils/zodSchema';
import { Button, Typography } from '@packages/webshop-design-system';
import { type FC, useCallback, useState } from 'react';
import { useFormContext } from 'react-hook-form';

interface PurchaseProps {
  formFields: Array<PersonDataField>;
}

const PURCHASER_FILL_FIELDS = [
  'firstName',
  'lastName',
  'email',
  'phoneNumber',
] as const;

const Purchaser: FC<PurchaseProps> = ({ formFields }) => {
  const [pendingIndex, setPendingIndex] = useState<number | null>(null);
  const { watch, setValue, getValues } = useFormContext<CheckoutValues>();
  const passengers = watch('passengers');

  const isDisabled = useCallback((passenger: CheckoutPassenger) => {
    if (!passenger) {
      return true;
    }

    return !PURCHASER_FILL_FIELDS.some((name) => Boolean(passenger[name]));
  }, []);

  const handleFillFromPassenger = useCallback(
    (passenger: CheckoutPassenger, index: number) => {
      setPendingIndex(index);

      const filled: Pick<
        CheckoutValues['purchaser'],
        (typeof PURCHASER_FILL_FIELDS)[number]
      > = {
        firstName: passenger.firstName,
        lastName: passenger.lastName,
        email: passenger.email,
        phoneNumber: passenger.phoneNumber,
      };

      setValue('purchaser', { ...getValues('purchaser'), ...filled });
      setTimeout(() => setPendingIndex(null), 500);
    },
    [getValues, setValue]
  );

  return (
    <div className="flex flex-col gap-3 tablet:flex-row tablet:gap-6">
      <div className="flex flex-col gap-3 tablet:flex-1 tablet:gap-4">
        <Typography variant="body2" className="text-neutral">
          <TransText i18nKey="fillWithDataFrom" />
          {':'}
        </Typography>
        <div className="flex flex-col gap-2">
          {passengers.map((passenger, index) => (
            <Button
              key={index}
              size="small"
              variant="secondary"
              isDisabled={isDisabled(passenger)}
              isPending={pendingIndex === index}
              className="grid"
              onClick={() => handleFillFromPassenger(passenger, index)}
              data-testid="purchaser-passenger-button"
            >
              <Typography variant="body2-bold" className="truncate">
                {getPassengerName(passenger, index)}
              </Typography>
            </Button>
          ))}
        </div>
      </div>
      <PassengerInfo
        formFields={formFields}
        prefix="purchaser"
        data-testid="purchaser-content"
      />
    </div>
  );
};

export default Purchaser;
